import { useDeferredValue, useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import { ShieldAlert } from 'lucide-react';
import SiteHeader from '@/components/SiteHeader';
import SiteFooter from '@/components/SiteFooter';
import { FactList, Faq, Prose, Section, linkClass } from '@/components/converter/PageParts';
import {
  Checkbox,
  HowTo,
  MoreTools,
  NextSteps,
  Segmented,
  ToolCard,
  ToolHead,
  ToolHero,
  btnPrimary,
  useGzipSize,
  useLocalOnlyBreadcrumbs,
  useMounted,
  type FaqItem,
} from '@/components/svg-tools/ToolParts';
import { SvgInput } from '@/components/svg-tools/SvgInput';
import { SvgPreview } from '@/components/svg-tools/SvgPreview';
import { CodeOutput } from '@/components/svg-tools/CodeOutput';
import { SAMPLE } from '@/components/svg-tools/sample';
import {
  byteLength,
  fileStem,
  formatBytes,
  formatChange,
  inspectSvg,
  normalizeSvg,
  parseSvg,
  type SvgInfo,
} from '@/utils/svg-tools/svg-source';
import { defaultOptimizeOptions, svgoConfig, type OptimizeOptions } from '@/utils/svg-tools/svgo-config';

const PATH = '/svg-viewer';
const TITLE = 'SVG Viewer: View, Preview and Optimize SVG Online | SVG.new';
const DESCRIPTION =
  'Paste or drop an SVG to preview it next to its code. Switch backgrounds, zoom, check the size and optimize it with SVGO. Free, and it runs in your browser.';

const faq: FaqItem[] = [
  {
    q: 'Is my SVG uploaded anywhere?',
    a: 'No. The viewer and the optimizer run in your browser. The SVG is read from the text box or from the file you open, and it is never sent to a server, so you can check private or unreleased artwork here.',
  },
  {
    q: 'Is it safe to preview an SVG that contains scripts?',
    a: 'Yes. The preview shows the SVG as an image, the way an <img> tag does, so <script> elements, event handlers such as onload and links to other files do not run or load. If the SVG has any, the viewer lists them. Optimize strips scripts, event handlers and javascript: URLs unless you turn that option off. It is not a full sanitizer, so if your own site accepts SVG uploads from other people, clean them with a sanitizer such as DOMPurify.',
  },
  {
    q: 'How much smaller does optimizing make an SVG?',
    a: 'It depends on the file. On four real logos we tried, the savings ran from 2% and 3% on two that had clearly been optimized before to 21% and 43% on the others. The viewer shows your file\'s size before and after, plain and gzipped.',
  },
  {
    q: 'Will optimizing change how my SVG looks?',
    a: "It shouldn't. The optimizer uses SVGO's default preset, which is designed to keep the picture the same: it removes comments, editor metadata and default values, rounds coordinates to 3 decimal places and shortens path data. It keeps the viewBox and the <title>. Compare the original and optimized previews before you use the file, and turn on Keep ids if CSS or JavaScript outside the SVG refers to its ids.",
  },
  {
    q: 'Why does my SVG look different here than on my website?',
    a: 'The preview loads the SVG as an image, so CSS from your page does not reach it, and images or web fonts it links to do not load. Text set in a font that is not installed on your computer falls back to a default font.',
  },
];

type Analysis = { ok: true; svg: string; fixes: string[]; info: SvgInfo; bytes: number } | { ok: false; error: string };

function analyze(text: string): Analysis {
  const normalized = normalizeSvg(text);
  const parsed = parseSvg(normalized.text);
  if (!parsed.ok) return parsed;
  return { ok: true, svg: normalized.text, fixes: normalized.fixes, info: inspectSvg(parsed.root), bytes: byteLength(text) };
}

// SVGO is about 190 KB gzipped, so it is fetched only when someone presses Optimize.
let svgoModule: Promise<typeof import('svgo/browser')> | null = null;
function loadSvgo() {
  if (!svgoModule) {
    svgoModule = import('svgo/browser').catch((err) => {
      svgoModule = null;
      throw err;
    });
  }
  return svgoModule;
}

interface Optimized {
  source: string;
  options: OptimizeOptions;
  data?: string;
  error?: string;
}

const sameOptions = (a: OptimizeOptions, b: OptimizeOptions) =>
  a.keepIds === b.keepIds && a.removeScripts === b.removeScripts && a.pretty === b.pretty;

export default function SvgViewerPage() {
  useLocalOnlyBreadcrumbs();
  const mounted = useMounted();
  const [text, setText] = useState(SAMPLE.text);
  const [fileName, setFileName] = useState(SAMPLE.name);
  const deferred = useDeferredValue(text);
  const analysis = useMemo(() => (mounted ? analyze(deferred) : null), [mounted, deferred]);

  const [optimizeOn, setOptimizeOn] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [options, setOptions] = useState<OptimizeOptions>(defaultOptimizeOptions);
  const [result, setResult] = useState<Optimized | null>(null);
  const [view, setView] = useState<'original' | 'optimized'>('optimized');

  const source = analysis?.ok ? analysis.svg : null;
  useEffect(() => {
    if (!optimizeOn || source === null) return;
    let cancelled = false;
    loadSvgo().then(
      ({ optimize }) => {
        if (cancelled) return;
        try {
          setResult({ source, options, data: optimize(source, svgoConfig(options)).data });
        } catch (err) {
          const reason = err instanceof Error ? err.message.split('\n')[0] : '';
          setResult({ source, options, error: `SVGO could not optimize this SVG. ${reason}`.trim() });
        }
      },
      () => {
        if (cancelled) return;
        setOptimizeOn(false);
        setLoadError("Couldn't load the optimizer. Check your connection and press Optimize again.");
      }
    );
    return () => {
      cancelled = true;
    };
  }, [optimizeOn, source, options]);

  const current = result && result.source === source && sameOptions(result.options, options) ? result : null;
  const optimizedData = current?.data ?? null;
  const showOptimized = view === 'optimized' && optimizedData !== null;

  const originalGzip = useGzipSize(analysis?.ok ? deferred : null);
  const optimizedGzip = useGzipSize(optimizedData);

  const info = analysis?.ok ? analysis.info : null;
  const stem = fileStem(fileName);

  return (
    <>
      <ToolHead
        path={PATH}
        title={TITLE}
        description={DESCRIPTION}
        socialTitle="SVG viewer and optimizer"
        appName="SVG.new SVG viewer"
        crumb="SVG viewer"
        featureList={[
          'Preview SVG code next to the rendered image',
          'Light, dark and checkered backgrounds',
          'Zoom from 10% to 1600%',
          'File size, gzipped size, dimensions and element count',
          'Optimize with SVGO in the browser and compare sizes',
          'Flags scripts, event handlers and external references',
        ]}
        faq={faq}
      />
      <div className="min-h-screen bg-cream-50 flex flex-col font-sans">
        <SiteHeader />

        <main className="flex-1">
          <ToolHero
            crumb="SVG viewer"
            before=""
            highlight="SVG viewer"
            after=" and optimizer"
            intro="Paste SVG code or open a file to see the picture next to its code. Switch the background, zoom in, then shrink the file with SVGO and copy or download it."
          >
            <ToolCard>
              <div className="grid min-w-0 lg:grid-cols-2">
                <div className="min-w-0 border-b border-cream-200 lg:border-b-0 lg:border-r">
                  <SvgInput
                    value={text}
                    sample={SAMPLE}
                    onChange={(next, name) => {
                      setText(next);
                      if (name) setFileName(name);
                    }}
                  />
                </div>
                <div className="min-w-0">
                  <SvgPreview
                    svg={analysis?.ok ? (showOptimized ? optimizedData : analysis.svg) : null}
                    width={info?.width ?? 120}
                    height={info?.height ?? 120}
                    empty={analysis && !analysis.ok ? analysis.error : 'The preview appears here.'}
                    toolbarExtra={
                      optimizedData !== null ? (
                        <Segmented
                          label="Show"
                          value={view}
                          onChange={setView}
                          options={[
                            { value: 'original', label: 'Original' },
                            { value: 'optimized', label: 'Optimized' },
                          ]}
                        />
                      ) : null
                    }
                  />
                </div>
              </div>

              <div className="border-t border-cream-200 px-4 py-3 text-[12.5px] text-ink-muted" aria-live="polite">
                {analysis?.ok && info ? (
                  <p className="flex flex-wrap gap-x-4 gap-y-1">
                    <span>
                      <span className="text-ink font-medium">{formatBytes(analysis.bytes)}</span>
                      {originalGzip !== null && <> · {formatBytes(originalGzip)} gzipped</>}
                    </span>
                    <span>
                      {+info.width.toFixed(2)} × {+info.height.toFixed(2)} px
                    </span>
                    {info.viewBox && <span className="font-mono text-[12px]">viewBox {info.viewBox}</span>}
                    <span>
                      {info.elements} elements, {info.paths} {info.paths === 1 ? 'path' : 'paths'}
                    </span>
                  </p>
                ) : (
                  <p>{analysis && !analysis.ok ? analysis.error : 'Reading the SVG…'}</p>
                )}
                {analysis?.ok && analysis.fixes.length > 0 && (
                  <p className="mt-1">{analysis.fixes.join(' ')}</p>
                )}
              </div>

              {info && info.risks.length > 0 && (
                <div className="mx-4 mb-3 flex gap-2.5 rounded-lg border border-amber/30 bg-amber/5 px-3 py-2.5 text-[13px] leading-relaxed text-ink-soft">
                  <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber" aria-hidden="true" />
                  <p>
                    This SVG contains {info.risks.join(', ')}. None of it runs or loads here, because the preview shows the
                    SVG as an image. It would run if you put the SVG inline in a web page.
                    {options.removeScripts && ' Optimize strips scripts, event handlers and javascript: URLs.'}
                  </p>
                </div>
              )}

              <div className="border-t border-cream-200 px-4 py-4">
                <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
                  <button
                    type="button"
                    className={btnPrimary}
                    disabled={!analysis?.ok || (optimizeOn && !current)}
                    onClick={() => {
                      setLoadError(null);
                      setView('optimized');
                      setOptimizeOn(true);
                    }}
                  >
                    {optimizeOn && !current && analysis?.ok ? 'Optimizing…' : 'Optimize with SVGO'}
                  </button>
                  <Checkbox checked={options.removeScripts} onChange={(v) => setOptions({ ...options, removeScripts: v })}>
                    Strip scripts and event handlers
                  </Checkbox>
                  <Checkbox checked={options.keepIds} onChange={(v) => setOptions({ ...options, keepIds: v })}>
                    Keep ids
                  </Checkbox>
                  <Checkbox checked={options.pretty} onChange={(v) => setOptions({ ...options, pretty: v })}>
                    Pretty print
                  </Checkbox>
                </div>
                {loadError && <p className="mt-3 text-[13px] text-clay">{loadError}</p>}
                {current?.error && <p className="mt-3 text-[13px] text-clay">{current.error}</p>}
                {analysis?.ok && optimizedData !== null && (
                  <div className="mt-4 space-y-3">
                    <p className="text-[14px] text-ink-soft" aria-live="polite">
                      <span className="font-semibold text-ink">
                        {formatBytes(analysis.bytes)} → {formatBytes(byteLength(optimizedData))}
                      </span>{' '}
                      ({formatChange(analysis.bytes, byteLength(optimizedData))})
                      {originalGzip !== null && optimizedGzip !== null && (
                        <>
                          {' '}
                          · gzipped {formatBytes(originalGzip)} → {formatBytes(optimizedGzip)} (
                          {formatChange(originalGzip, optimizedGzip)})
                        </>
                      )}
                    </p>
                    <CodeOutput
                      label="Optimized SVG"
                      code={optimizedData}
                      download={{ fileName: `${stem}.min.svg`, type: 'image/svg+xml' }}
                      wrap={!options.pretty}
                      heightClass="max-h-[280px]"
                    />
                  </div>
                )}
              </div>
            </ToolCard>
          </ToolHero>

          <NextSteps
            agentText={
              <>
                Your coding agent can already read and edit SVG code. Add the SVG.new MCP server and it can also turn PNG,
                JPG and WebP files into SVG, recolor SVGs and cut their colors, right in your repo.
              </>
            }
          />

          <Section heading="How to view and optimize an SVG">
            <HowTo
              steps={[
                {
                  title: 'Paste the code or open the file',
                  text: 'Paste SVG markup into the code box, drop an .svg file on it or press Open file. The preview follows as you type, so you can edit the code and watch the result.',
                },
                {
                  title: 'Check it on light, dark and checkered backgrounds',
                  text: 'The checkerboard shows which parts are transparent; the dark background shows white shapes and strokes that disappear on white. Zoom in to inspect edges, or press the percentage to see the SVG at its actual size.',
                },
                {
                  title: 'Optimize, compare and save',
                  text: 'Press Optimize with SVGO. The sizes before and after appear under the button, and the preview switches to the optimized file so you can compare it with the original. Then copy the code or download the .svg file.',
                },
              ]}
            />
          </Section>

          <Section heading="What the optimizer changes" tone="cream">
            <FactList
              items={[
                {
                  term: 'Removed',
                  text: 'Comments, the XML declaration, editor metadata from Inkscape, Illustrator and Sketch, empty groups, unused definitions and attributes that only repeat the default value.',
                },
                {
                  term: 'Shortened',
                  text: 'Path data and numbers (rounded to 3 decimal places), colors (#ffffff becomes #fff), transforms, and ids, which become one or two letters.',
                },
                {
                  term: 'Kept',
                  text: 'The viewBox, the <title>, data and aria attributes, and everything that changes how the SVG looks.',
                },
                {
                  term: 'Options',
                  text: 'Keep ids leaves ids as they are, for CSS or scripts outside the file. Strip scripts and event handlers is on by default and also removes javascript: URLs. Pretty print indents the output so it is easier to read.',
                },
              ]}
            />
            <div className="mt-6">
              <Prose>
                <p>
                  Going on to use the SVG in code? Turn it into a React component with the{' '}
                  <Link href="/svg-to-react" className={linkClass}>SVG to JSX converter</Link>, or into a CSS background
                  with <Link href="/svg-to-data-uri" className={linkClass}>SVG to data URI</Link>.
                </p>
              </Prose>
            </div>
          </Section>

          <Section heading="Questions about the SVG viewer">
            <Faq items={faq} />
          </Section>

          <MoreTools current={PATH} />
        </main>

        <SiteFooter />
      </div>
    </>
  );
}
